/**
 * 本地存储工具
 * 封装 localStorage 和 sessionStorage，支持过期时间
 */

const STORAGE_PREFIX = 'foodlabel_';

/**
 * 存储类型
 */
export const StorageType = {
  LOCAL: 'localStorage',
  SESSION: 'sessionStorage'
};

/**
 * 获取存储对象
 */
const getStorage = (type = StorageType.LOCAL) => {
  return type === StorageType.SESSION ? sessionStorage : localStorage;
};

/**
 * 生成完整的key
 */
const getFullKey = (key) => {
  return `${STORAGE_PREFIX}${key}`;
};

/**
 * 设置存储项
 * @param {string} key - 键名
 * @param {*} value - 值
 * @param {number} expire - 过期时间（分钟），0表示永不过期
 * @param {string} type - 存储类型
 */
export const setItem = (key, value, expire = 0, type = StorageType.LOCAL) => {
  try {
    const storage = getStorage(type);
    const fullKey = getFullKey(key);
    
    const item = {
      value,
      expire: expire > 0 ? Date.now() + expire * 60 * 1000 : 0,
      timestamp: Date.now()
    };
    
    storage.setItem(fullKey, JSON.stringify(item));
    return true;
  } catch (error) {
    console.error('Storage setItem error:', error);
    return false;
  }
};

/**
 * 获取存储项
 * @param {string} key - 键名
 * @param {*} defaultValue - 默认值
 * @param {string} type - 存储类型
 */
export const getItem = (key, defaultValue = null, type = StorageType.LOCAL) => {
  try {
    const storage = getStorage(type);
    const fullKey = getFullKey(key);
    const itemStr = storage.getItem(fullKey);
    
    if (!itemStr) {
      return defaultValue;
    }
    
    const item = JSON.parse(itemStr);
    
    // 检查是否过期
    if (item.expire && item.expire < Date.now()) {
      storage.removeItem(fullKey);
      return defaultValue;
    }
    
    return item.value;
  } catch (error) {
    console.error('Storage getItem error:', error);
    return defaultValue;
  }
};

/**
 * 移除存储项
 */
export const removeItem = (key, type = StorageType.LOCAL) => {
  try {
    const storage = getStorage(type);
    const fullKey = getFullKey(key);
    storage.removeItem(fullKey);
    return true;
  } catch (error) {
    console.error('Storage removeItem error:', error);
    return false;
  }
};

/**
 * 清空所有存储项（只清空带前缀的）
 */
export const clear = (type = StorageType.LOCAL) => {
  try {
    const storage = getStorage(type);
    const keys = Object.keys(storage);
    
    keys.forEach(key => {
      if (key.startsWith(STORAGE_PREFIX)) {
        storage.removeItem(key);
      }
    });
    
    return true;
  } catch (error) {
    console.error('Storage clear error:', error);
    return false;
  }
};

/**
 * 获取所有存储项的键名
 */
export const getAllKeys = (type = StorageType.LOCAL) => {
  try {
    const storage = getStorage(type);
    const keys = Object.keys(storage);
    
    return keys
      .filter(key => key.startsWith(STORAGE_PREFIX))
      .map(key => key.replace(STORAGE_PREFIX, ''));
  } catch (error) {
    console.error('Storage getAllKeys error:', error);
    return [];
  }
};

/**
 * 检查存储项是否存在
 */
export const hasItem = (key, type = StorageType.LOCAL) => {
  return getItem(key, undefined, type) !== undefined;
};

/**
 * 获取存储项的大小（字节）
 */
export const getSize = (type = StorageType.LOCAL) => {
  try {
    const storage = getStorage(type);
    let size = 0;
    
    for (let key in storage) {
      if (storage.hasOwnProperty(key) && key.startsWith(STORAGE_PREFIX)) {
        size += storage[key].length + key.length;
      }
    }
    
    return size;
  } catch (error) {
    console.error('Storage getSize error:', error);
    return 0;
  }
};

/**
 * 清理过期的存储项
 */
export const cleanExpired = (type = StorageType.LOCAL) => {
  try {
    const storage = getStorage(type);
    const keys = Object.keys(storage);
    let count = 0;
    
    keys.forEach(key => {
      if (key.startsWith(STORAGE_PREFIX)) {
        try {
          const item = JSON.parse(storage.getItem(key));
          if (item.expire && item.expire < Date.now()) {
            storage.removeItem(key);
            count++;
          }
        } catch (e) {
          // 如果解析失败，也删除
          storage.removeItem(key);
          count++;
        }
      }
    });
    
    return count;
  } catch (error) {
    console.error('Storage cleanExpired error:', error);
    return 0;
  }
};

/**
 * useStorage Hook
 */
export const useStorage = (key, defaultValue = null, expire = 0, type = StorageType.LOCAL) => {
  const get = () => getItem(key, defaultValue, type);
  const set = (value) => setItem(key, value, expire, type);
  const remove = () => removeItem(key, type);
  
  return [get(), set, remove];
};

/**
 * 常用存储key
 */
export const STORAGE_KEYS = {
  TOKEN: 'auth_token',
  USER_INFO: 'user_info',
  REMEMBER_ME: 'remember_me',
  LAST_LOGIN: 'last_login',
  THEME: 'theme',
  LANGUAGE: 'language',
  LABEL_DRAFT: 'label_draft',
  SEARCH_HISTORY: 'search_history',
};

export default {
  setItem,
  getItem,
  removeItem,
  clear,
  getAllKeys,
  hasItem,
  getSize,
  cleanExpired,
  useStorage,
  StorageType,
  STORAGE_KEYS
};

